import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { OrganizationType } from '@portal/api/enums/organization-type';

export type AppContext = 'portal' | 'club';

@Injectable({
    providedIn: 'root',
})
export class AppContextService {
    constructor(@Inject(DOCUMENT) private readonly _document: Document) {
    }

    getContext(hostname = this.getLocation()?.hostname ?? ''): AppContext {
        const normalizedHostname = hostname.toLowerCase();
        const firstLabel = normalizedHostname.split('.')[0] ?? '';

        if (this.matchesContextLabel(firstLabel, 'club')) {
            return 'club';
        }

        if (this.matchesContextLabel(firstLabel, 'portal')) {
            return 'portal';
        }

        throw new Error(`Unable to resolve app context from hostname "${hostname}"`);
    }

    allowedOrgType(hostname = this.getLocation()?.hostname ?? ''): OrganizationType.Party | OrganizationType.Association {
        return this.getContext(hostname) === 'club'
            ? OrganizationType.Association
            : OrganizationType.Party;
    }

    isOrgTypeAllowed(type: OrganizationType | string | number | null | undefined, hostname = this.getLocation()?.hostname ?? ''): boolean {
        return this.normalizeOrgType(type) === this.allowedOrgType(hostname);
    }

    defaultRoute(hostname = this.getLocation()?.hostname ?? ''): string {
        try {
            return this.getContext(hostname) === 'club'
                ? '/organizations/dashboard'
                : '/parties';
        } catch {
            return '/parties';
        }
    }

    private matchesContextLabel(label: string, context: AppContext): boolean {
        return this.extractContextSuffix(label, context) !== null;
    }

    private extractContextSuffix(label: string, expectedContext?: AppContext): string | null {
        const match = label.match(/^(portal|club)(-.+)?$/);

        if (!match) {
            return null;
        }

        const context = match[1] as AppContext;
        if (expectedContext && context !== expectedContext) {
            return null;
        }

        return match[2] ?? '';
    }

    private normalizeOrgType(type: OrganizationType | string | number | null | undefined): OrganizationType | null {
        if (type === null || type === undefined) {
            return null;
        }

        if (typeof type === 'number') {
            return type as OrganizationType;
        }

        if (typeof type === 'string') {
            const trimmedType = type.trim();
            const numericType = Number(trimmedType);

            if (!Number.isNaN(numericType) && trimmedType !== '') {
                return numericType as OrganizationType;
            }

            switch (trimmedType.toLowerCase()) {
                case 'association':
                    return OrganizationType.Association;
                case 'party':
                    return OrganizationType.Party;
                case 'club':
                    return OrganizationType.Club;
                case 'tuna':
                    return OrganizationType.Tuna;
                default:
                    return null;
            }
        }

        return null;
    }

    private getLocation(): Location | null {
        return this._document.defaultView?.location ?? globalThis.location ?? null;
    }
}
